import React, { useState } from 'react';
import { Eye, Send, Sun, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import { waApi, errMsg } from './waApi';

const KINDS = [
  { key: 'daily_digest', label: 'Morning digest', icon: Sun, when: 'Every day 08:30 IST', what: 'Receivables · payables · stock run-outs · today\'s plan · dispatch due' },
  { key: 'weekly_report', label: 'Weekly report', icon: CalendarRange, when: 'Monday 09:00 IST', what: 'RM price hikes · who reduced orders · delayed payments (bill no + ₹) · production' },
];

export const WaReports = ({ users }) => {
  const [kind, setKind] = useState('daily_digest');
  const [userId, setUserId] = useState(users.find(u => u.whatsapp.enabled)?.id || users[0]?.id || '');
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    setBusy(true);
    try { const r = await waApi.preview(kind, userId); setPreview(r.text); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doSend = async (all) => {
    setBusy(true);
    try {
      const r = await waApi.sendNow(kind, all ? null : userId);
      const n = all ? (r.sent || []).length : 1;
      const simulated = all ? (r.sent || []).every(s => s.delivery?.simulated) : r.delivery?.simulated;
      toast[simulated ? 'info' : 'success'](simulated ? `Simulator mode — ${n} report(s) logged, not delivered` : `Sent ${n} report(s)`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-4" data-testid="wa-reports">
      <div className="lg:col-span-2 space-y-3">
        {KINDS.map(k => (
          <button key={k.key} onClick={() => setKind(k.key)} data-testid={`wa-report-kind-${k.key}`}
            className={`w-full text-left rounded-2xl border p-4 transition-colors ${kind === k.key ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'}`}>
            <div className="flex items-center gap-2 text-zinc-100 font-semibold text-sm"><k.icon size={14} className="text-emerald-400" /> {k.label}</div>
            <div className="text-[11px] text-zinc-400 mt-1">{k.what}</div>
            <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">{k.when} · auto-push to opted-in users</div>
          </button>
        ))}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Preview as</div>
          <select value={userId} onChange={e => setUserId(e.target.value)} data-testid="wa-report-user-select"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2 text-xs text-zinc-100">
            {users.map(u => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
          </select>
          <div className="flex gap-2 pt-1">
            <button onClick={doPreview} disabled={busy} data-testid="wa-report-preview-btn"
              className="flex-1 text-xs flex items-center justify-center gap-1 px-3 py-2 rounded-xl border border-zinc-700 text-zinc-200 hover:border-emerald-500/50 disabled:opacity-50"><Eye size={12} /> Preview</button>
            <button onClick={() => doSend(false)} disabled={busy} data-testid="wa-report-send-user-btn"
              className="flex-1 text-xs flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-[#25D366] text-black font-semibold hover:bg-[#1ebe5a] disabled:opacity-50"><Send size={12} /> Send to user</button>
          </div>
          <button onClick={() => doSend(true)} disabled={busy} data-testid="wa-report-send-all-btn"
            className="w-full text-xs px-3 py-2 rounded-xl border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50">Send now to everyone opted in</button>
        </div>
      </div>
      <div className="lg:col-span-3 bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">WhatsApp preview</div>
        {preview ? (
          <div className="bg-zinc-800 text-zinc-100 rounded-2xl rounded-bl-sm px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed max-h-[520px] overflow-y-auto" data-testid="wa-report-preview">{preview}</div>
        ) : (
          <div className="text-xs text-zinc-500 py-16 text-center">Pick a report and a user, then hit Preview. Sections follow that user's scopes — e.g. no money lines for someone without price visibility.</div>
        )}
      </div>
    </div>
  );
};
